import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { api } from '../services/api';
import { PublicUserSummary } from '../types/auth';
import { useAuth } from './AuthContext';

/**
 * The current user's blocked list («Чёрный список»). One source of truth so the
 * chat menu, guest profile and the Settings blacklist stay in sync. Blocking is
 * server-enforced (the BE stops delivering the blocked user's messages); this
 * just mirrors that state and drives «Заблокировать ↔ Разблокировать».
 */
export interface BlockedUsersContextValue {
  blocked: PublicUserSummary[];
  isBlocked: (userId: string) => boolean;
  /** Resolves `null` when the server confirmed the block, or a human-readable
   *  error string on failure (the optimistic entry is rolled back) so callers
   *  can surface exactly why the server rejected it. */
  block: (userId: string, summary?: Partial<PublicUserSummary>) => Promise<string | null>;
  unblock: (userId: string) => Promise<boolean>;
  refresh: () => void;
}

/** Ids may arrive as the raw UUID or the Stream `user_<id>` form — normalise. */
const norm = (id: string) => (id || '').replace(/^user_/, '');

/** Текст автоматической жалобы, которую блокировка отправляет модерации. */
export const BLOCK_REPORT_DESCRIPTION =
  'Автоматическое уведомление: пользователь заблокирован другим пользователем из приложения. Требуется проверка его контента и поведения.';

const BlockedUsersContext = createContext<BlockedUsersContextValue | null>(null);

export const BlockedUsersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [blocked, setBlocked] = useState<PublicUserSummary[]>([]);
  const [ids, setIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(() => {
    if (!isAuthenticated) { setBlocked([]); setIds(new Set()); return; }
    api.getBlockedUsers()
      .then((list) => {
        setBlocked((prev) => {
          const serverIds = new Set(list.map((u) => norm(u.id)));
          // The BE blocked-list read model lags a beat behind the block POST (and
          // the concurrent DM delete in «Удалить и заблокировать»). Carry any
          // locally-known blocked entry the server hasn't echoed back yet so it
          // doesn't flicker out of «Чёрный список» right after blocking.
          const carry = prev.filter((u) => !serverIds.has(norm(u.id)));
          const merged = [...list, ...carry];
          setIds(new Set(merged.map((u) => norm(u.id))));
          return merged;
        });
      })
      .catch(() => { /* keep current on transient failure */ });
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  const isBlocked = useCallback((userId: string) => ids.has(norm(userId)), [ids]);

  const block = useCallback(async (userId: string, summary?: Partial<PublicUserSummary>) => {
    const id = norm(userId);
    setIds((prev) => new Set(prev).add(id));
    if (summary) {
      setBlocked((prev) => (prev.some((u) => norm(u.id) === id) ? prev : [...prev, {
        id,
        displayName: summary.displayName ?? '',
        avatarUrl: summary.avatarUrl ?? null,
        bio: null,
        intention: null,
        location: null,
        isMaster: !!summary.isMaster,
        isOnline: false,
        joinedAt: '',
      }]));
    }
    try {
      await api.blockChatUser(id);
      // CORE-840 — App Store Guideline 1.2 требует, чтобы блокировка ещё и
      // уведомляла разработчика о поведении заблокированного. Отдельного
      // канала для этого нет, поэтому кладём запись в тот же поток жалоб
      // (POST /api/reports), который читает модерация. Best-effort: если
      // жалоба не ушла, сама блокировка всё равно состоялась — гасить её
      // из-за уведомления нельзя.
      api.createReport({
        targetType: 'user',
        targetId: id,
        reason: 'other',
        description: BLOCK_REPORT_DESCRIPTION,
      }).catch(() => { /* блокировка важнее уведомления */ });
      refresh(); // reconcile with the server (canonical name/avatar)
      return null;
    } catch (e: any) {
      setIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      setBlocked((prev) => prev.filter((u) => norm(u.id) !== id));
      // 404 = the block endpoint isn't live on this environment yet (the BE
      // feature is mid-rollout). Other statuses surface the server message so a
      // real failure isn't hidden behind a generic line.
      if (e?.status === 404) return 'Блокировка пока не подключена на сервере — обратитесь к поддержке.';
      const status = e?.status ? ` (${e.status})` : '';
      const msg = e?.message || 'Не удалось связаться с сервером.';
      return `${msg}${status}`;
    }
  }, [refresh]);

  const unblock = useCallback(async (userId: string) => {
    const id = norm(userId);
    setIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    setBlocked((prev) => prev.filter((u) => norm(u.id) !== id));
    try {
      await api.unblockChatUser(id);
      return true;
    } catch {
      refresh(); // rollback to the server truth
      return false;
    }
  }, [refresh]);

  const value = useMemo(
    () => ({ blocked, isBlocked, block, unblock, refresh }),
    [blocked, isBlocked, block, unblock, refresh],
  );

  return <BlockedUsersContext.Provider value={value}>{children}</BlockedUsersContext.Provider>;
};

export function useBlockedUsers(): BlockedUsersContextValue {
  const ctx = useContext(BlockedUsersContext);
  if (!ctx) throw new Error('useBlockedUsers must be used within BlockedUsersProvider');
  return ctx;
}
